import { useCallback, useState } from 'react'
import { readFileAsText } from '~/services/onboardingService'
import { IndexRuntimeSettings } from '~/types/ResponseType'
import { BetterToast } from '~/utils/toast'

interface FileInfo {
  name: string
  size: number
}

type UploadState = {
  status: 'uploading' | 'uploaded' | 'error' | null
  progress?: number
  error?: string
  file?: FileInfo
  virusScanStatus?: string
  virusScanMessage?: string
}

export function useFileUpload(
  drawerRuntimeSetting: IndexRuntimeSettings,
  updateDrawerRuntimeSetting: (
    updater: (prev: IndexRuntimeSettings) => IndexRuntimeSettings
  ) => void
) {
  const [uploadState, setUploadState] = useState<UploadState>({ status: null })
  const [securityUploadState, setSecurityUploadState] = useState<UploadState>({ status: null })

  const handleFileUpload = useCallback(
    async (file: File, isSettingsSecurityXML = false) => {
      if (
        (isSettingsSecurityXML && drawerRuntimeSetting.settings_security_xml_raw_txt) ||
        (!isSettingsSecurityXML && drawerRuntimeSetting.settings_xml_raw_txt)
      ) {
        BetterToast.error(`${isSettingsSecurityXML ? 'Security ' : ''}Settings XML already exists`)
        return
      }

      const fileInfo = {
        name: file.name,
        size: file.size
      }

      // Validate file type - Improved file type validation, mainly check file extension
      if (!file.name.toLowerCase().endsWith('.xml')) {
        const setUploadStateFn = isSettingsSecurityXML ? setSecurityUploadState : setUploadState
        setUploadStateFn({
          status: 'error',
          error: 'Upload failed: Invalid file format. Please upload a xml file.',
          file: fileInfo
        })
        return
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        const setUploadStateFn = isSettingsSecurityXML ? setSecurityUploadState : setUploadState
        setUploadStateFn({
          status: 'error',
          error: 'Upload failed: Maximum file size exceeded.',
          file: fileInfo
        })
        return
      }

      const setUploadStateFn = isSettingsSecurityXML ? setSecurityUploadState : setUploadState
      setUploadStateFn({
        status: 'uploading',
        progress: 10,
        file: fileInfo
      })

      try {
        // Read the XML file as text
        const xmlContent = await readFileAsText(file)

        // Simulate progress
        let currentProgress = 10
        const interval = setInterval(() => {
          currentProgress += 10
          if (currentProgress >= 100) {
            clearInterval(interval)
            setUploadStateFn(prev => ({
              ...prev,
              status: 'uploaded',
              progress: 100
            }))
            // Update settings after progress completes
            updateDrawerRuntimeSetting((prev: IndexRuntimeSettings) => ({
              ...prev,
              settings_xml_raw_txt: isSettingsSecurityXML ? prev.settings_xml_raw_txt : xmlContent,
              settings_security_xml_raw_txt: isSettingsSecurityXML
                ? xmlContent
                : prev.settings_security_xml_raw_txt,
              settings_xml_filename: isSettingsSecurityXML ? prev.settings_xml_filename : file.name,
              settings_security_xml_filename: isSettingsSecurityXML
                ? file.name
                : prev.settings_security_xml_filename
            }))
          } else {
            setUploadStateFn(prev => ({
              ...prev,
              progress: currentProgress
            }))
          }
        }, 200) // Adjust timing as needed
      } catch (error) {
        console.error('Error uploading settings XML:', error)
        setUploadStateFn(prev => ({
          ...prev,
          status: 'error',
          error: 'Upload failed'
        }))
      }
    },
    [
      drawerRuntimeSetting.settings_xml_raw_txt,
      drawerRuntimeSetting.settings_security_xml_raw_txt,
      updateDrawerRuntimeSetting
    ]
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, isSettingsSecurityXML = false) => {
      const files = e.target.files
      if (files && files.length > 0) {
        handleFileUpload(files[0], isSettingsSecurityXML)
      } else {
        console.log('No files selected')
      }
    },
    [handleFileUpload]
  )

  const resetUploadState = useCallback((isSecurityUpload = false) => {
    if (isSecurityUpload) {
      setSecurityUploadState({ status: null })
    } else {
      setUploadState({ status: null })
    }
  }, [])

  return {
    uploadState,
    securityUploadState,
    handleFileUpload,
    handleFileSelect,
    resetUploadState,
    setUploadState,
    setSecurityUploadState
  }
}
